/**
 * HRDashboardPage - Tableau de bord RH
 * Vue d'ensemble avec statistiques, alertes et indicateurs clés
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { 
  Users, FileSignature, Receipt, AlertTriangle, Calendar,
  Stethoscope, GraduationCap, Clock, ChevronRight, Loader2,
  TrendingUp, UserPlus, FileText, CalendarDays, CheckCircle,
  XCircle, AlertCircle, BarChart3, Download, Mail, Send
} from "lucide-react";
import { formatDateFr } from "../../components/ui/date-picker-fr";
import { getAlertTypeLabel, getAlertPriorityColor } from "../../lib/hr-constants";
import { toast } from "sonner";
import AdvancePaymentsMonthlyDashboard from "../../components/rh/AdvancePaymentsMonthlyDashboard";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HRDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [payrollWidget, setPayrollWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingUrssaf, setExportingUrssaf] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, alertsRes, payrollRes] = await Promise.all([
        axios.get(`${API}/api/hr/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/hr/alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/hr/payroll-variables/dashboard-widget`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null }))
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data.alerts || []);
      setPayrollWidget(payrollRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Export CSV
  const handleExportCsv = async () => {
    setExportingCsv(true);
    try {
      const response = await axios.get(`${API}/api/hr/export/employees/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_salaries_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export CSV error:", err);
    } finally {
      setExportingCsv(false);
    }
  };

  // Export URSSAF
  const handleExportUrssaf = async () => {
    setExportingUrssaf(true);
    try {
      const year = new Date().getFullYear();
      const response = await axios.get(`${API}/api/hr/export/urssaf?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_urssaf_${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export URSSAF error:", err);
    } finally {
      setExportingUrssaf(false);
    }
  };

  // Send email notifications
  const handleSendNotifications = async () => {
    setSendingNotifications(true);
    try {
      const response = await axios.post(
        `${API}/api/hr/notifications/send-digest`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Notifications envoyées", {
          description: `${response.data.alerts_count} alerte(s) envoyée(s) par email`
        });
      }
    } catch (err) {
      console.error("Send notifications error:", err);
      toast.error("Erreur", {
        description: "Impossible d'envoyer les notifications"
      });
    } finally {
      setSendingNotifications(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const criticalAlerts = alerts.filter(a => a.priority === "critical" || a.priority === "high");

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="hr-dashboard-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
            Tableau de bord RH
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">Vue d'ensemble des ressources humaines</p>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/rh/salaries")} className="text-xs sm:text-sm">
            <Users className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Salariés</span>
            <span className="sm:hidden">Salariés</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportCsv}
            disabled={exportingCsv}
            data-testid="export-csv-btn"
            className="text-xs sm:text-sm"
          >
            {exportingCsv ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-1 sm:mr-2" />}
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">CSV</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportUrssaf}
            disabled={exportingUrssaf}
            data-testid="export-urssaf-btn"
            className="text-xs sm:text-sm col-span-2 sm:col-span-1"
          >
            {exportingUrssaf ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-1 sm:mr-2" />}
            <span className="hidden sm:inline">Export URSSAF</span>
            <span className="sm:hidden">URSSAF</span>
          </Button>
        </div>
      </div>

      {/* Critical Alerts Banner */}
      {criticalAlerts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-red-800">{criticalAlerts.length} alerte(s) urgente(s)</p>
                <p className="text-sm text-red-600">
                  {criticalAlerts.slice(0, 2).map(a => a.title).join(", ")}
                  {criticalAlerts.length > 2 && ` et ${criticalAlerts.length - 2} autre(s)`}
                </p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  className="flex-1 sm:flex-none border-red-300 text-red-700 hover:bg-red-100" 
                  onClick={() => document.getElementById('alerts-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Voir les alertes
                </Button>
                <Button 
                  variant="default"
                  className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleSendNotifications}
                  disabled={sendingNotifications}
                  data-testid="send-notifications-btn"
                >
                  {sendingNotifications ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  <span className="hidden sm:inline">Envoyer par email</span>
                  <span className="sm:hidden">Email</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          title="Effectif total"
          value={dashboard?.employees?.total || 0}
          icon={Users}
          color="blue"
          onClick={() => navigate("/rh/salaries")}
        />
        <StatCard
          title="Actifs"
          value={dashboard?.employees?.active || 0}
          icon={CheckCircle}
          color="green"
        />
        <StatCard
          title="Entrées récentes"
          value={dashboard?.employees?.recent_hires || 0}
          subtitle="30 derniers jours"
          icon={UserPlus}
          color="emerald"
        />
        <StatCard
          title="CDI"
          value={dashboard?.contracts?.cdi || 0}
          icon={FileSignature}
          color="indigo"
          onClick={() => navigate("/rh/contrats")}
        />
        <StatCard
          title="CDD / Autres"
          value={dashboard?.contracts?.cdd_and_others || 0}
          icon={Clock}
          color="amber"
          onClick={() => navigate("/rh/contrats")}
        />
        <StatCard
          title="Absences en attente"
          value={dashboard?.absences?.pending || 0}
          icon={CalendarDays}
          color="purple"
          alert={dashboard?.absences?.pending > 0}
        />
      </div>

      {/* Secondary Stats */}
      <AdvancePaymentsMonthlyDashboard token={token} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate("/rh/contrats")}>          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Contrats à échéance</p>
                <p className="text-3xl font-bold text-orange-600">{dashboard?.contracts?.expiring_soon || 0}</p>
                <p className="text-xs text-slate-400 mt-1">Dans les 90 prochains jours</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Documents à renouveler</p>
                <p className="text-3xl font-bold text-red-600">{dashboard?.documents?.expiring_soon || 0}</p>
                <p className="text-xs text-slate-400 mt-1">Dans les 60 prochains jours</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Formations {new Date().getFullYear()}</p>
                <p className="text-3xl font-bold text-indigo-600">
                  {dashboard?.trainings?.completed || 0}/{dashboard?.trainings?.total_this_year || 0}
                </p>
                <p className="text-xs text-slate-400 mt-1">Terminées / Total</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payroll Variables Widget */}
      {payrollWidget && (
        <Card className="hover:shadow-md transition-shadow cursor-pointer border-blue-200 bg-gradient-to-r from-blue-50 to-white" onClick={() => navigate("/rh/variables-paie")}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-5 h-5 text-blue-600" />
                  <p className="font-semibold text-slate-800">Variables de paie - {payrollWidget.month_name} {payrollWidget.year}</p>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold text-blue-600">{payrollWidget.variables_created || 0}</span>
                    <span className="text-slate-500">/ {payrollWidget.total_employees || 0} salariés</span>
                  </div>
                  <div className="h-2 flex-1 max-w-32 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${payrollWidget.completion_percent || 0}%` }}
                    />
                  </div>
                  <span className="text-slate-500">{payrollWidget.completion_percent || 0}%</span>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                  <span className="text-orange-600">{payrollWidget.draft_count || 0} à envoyer</span>
                  <span className="text-green-600">{payrollWidget.sent_count || 0} envoyés</span>
                  {payrollWidget.auto_send_enabled && payrollWidget.next_auto_send && (
                    <span className="flex items-center gap-1 text-blue-500">
                      <Calendar className="w-3 h-3" />
                      Envoi auto le {new Date(payrollWidget.next_auto_send).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                    </span>
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts Section */}
      <div id="alerts-section">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Alertes et échéances
                {alerts.length > 0 && (
                  <Badge className="bg-amber-500">{alerts.length}</Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-2" />
                <p>Aucune alerte en cours</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-3">
                  {alerts.map(alert => (
                    <AlertCard 
                      key={alert.id} 
                      alert={alert}
                      onClick={() => {
                        if (alert.employee_id) {
                          navigate(`/rh/salaries/${alert.employee_id}`);
                        }
                      }}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <QuickActionCard
          title="Nouveau salarié"
          icon={UserPlus}
          color="blue"
          onClick={() => navigate("/rh/salaries?action=new")}
        />
        <QuickActionCard
          title="Fiches de paie"
          icon={Receipt}
          color="green"
          onClick={() => navigate("/rh/fiches-paie")}
        />
        <QuickActionCard
          title="Contrats"
          icon={FileSignature}
          color="indigo"
          onClick={() => navigate("/rh/contrats")}
        />
        <QuickActionCard
          title="Corbeille"
          icon={XCircle}
          color="slate"
          onClick={() => navigate("/rh/corbeille")}
        />
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, onClick, alert }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-green-50 text-green-600 border-green-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    red: "bg-red-50 text-red-600 border-red-100",
  };

  return (
    <Card 
      className={`border ${colors[color]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${alert ? 'ring-2 ring-amber-400' : ''}`}
      onClick={onClick}
    >
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${colors[color]} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">{title}</p>
            <p className="text-xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AlertCard({ alert, onClick }) {
  const priorityIcons = {
    critical: AlertTriangle,
    high: AlertCircle,
    medium: Clock,
    low: FileText,
  };
  const Icon = priorityIcons[alert.priority] || AlertCircle;

  return (
    <div 
      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50 transition-colors ${getAlertPriorityColor(alert.priority)}`}
      onClick={onClick}
    >
      <Icon className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm">{alert.title}</span>
          {alert.days_remaining !== null && alert.days_remaining !== undefined && (
            <Badge variant="secondary" className="text-xs">
              {alert.days_remaining <= 0 ? "Dépassé" : `${alert.days_remaining}j`}
            </Badge>
          )}
        </div>
        <p className="text-sm opacity-80">{alert.message}</p>
        {alert.employee_name && (
          <p className="text-xs opacity-60 mt-1">{alert.employee_name}</p>
        )}
      </div>
      <ChevronRight className="w-4 h-4 opacity-50" />
    </div>
  );
}

function QuickActionCard({ title, icon: Icon, color, onClick }) {
  const colors = {
    blue: "bg-blue-500 hover:bg-blue-600",
    green: "bg-green-500 hover:bg-green-600",
    indigo: "bg-indigo-500 hover:bg-indigo-600",
    slate: "bg-slate-500 hover:bg-slate-600",
  };

  return (
    <Card 
      className={`${colors[color]} text-white cursor-pointer transition-colors`}
      onClick={onClick}
    >
      <CardContent className="py-4 flex items-center justify-center gap-2">
        <Icon className="w-5 h-5" />
        <span className="font-medium">{title}</span>
      </CardContent>
    </Card>
  );
}
